// Dynamic XML builders for the Shipment Schedule ID success pop-up.
//
// buildShipmentOrderCreateXml(...)   -> "Shipment_Order_Create"   (request submitted
//                                        to the Freight Forwarder system, built from the
//                                        Shipment Schedule ID Generation Form values)
// buildShipmentOrderResponseXml(...) -> "Shipment_Order_Response" (response returned by the
//                                        Freight Forwarder system — orderNumber is the
//                                        Freight Forwarder Schedule ID from the response)
//
// Tag → source mapping follows CHR_OrderCreate_Form_Mapping.xlsx
// ("XML Tree → Source" sheet). Each leaf tag is sourced as:
//   Form      – taken from the Shipment Schedule ID Generation Form values
//   Config    – config default (overridable via data.*), e.g. currencyCode, company names
//   Constant  – literal value
//   Derived   – computed (e.g. SHID+'-PU', isTemperatureSensitive)
//   Generated – the generated Shipment Schedule ID
//   Empty/TBD – left empty / assigned by CHR

// ─── small helpers ──────────────────────────────────────────────────────────

// Escape the five XML-significant characters so free-text form values stay valid XML.
const esc = (val) => {
  if (val == null) return '';
  return String(val)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

// Phone numbers in the XML are digits only (no "+", spaces or dashes).
const phoneDigits = (val) => (val == null ? '' : String(val).replace(/\D/g, ''));

// stateProvinceCode: blank when the form value is empty or a placeholder dash.
const stateCode = (val) => {
  const v = (val == null ? '' : String(val)).trim();
  return !v || v === '-' ? '' : v;
};

// specialInstructions: 'NA' (any case) collapses to an empty element.
const cleanNA = (val) => {
  const v = (val == null ? '' : String(val)).trim();
  return !v || v.toUpperCase() === 'NA' ? '' : v;
};

// Map common country names to ISO-2 codes; fall back to the value as entered.
const COUNTRY_CODES = {
  'united states': 'US', 'usa': 'US', 'us': 'US',
  'united kingdom': 'GB', 'uk': 'GB',
  'ireland': 'IE',
  'netherlands': 'NL', 'the netherlands': 'NL',
  'germany': 'DE', 'france': 'FR', 'india': 'IN',
  'switzerland': 'CH', 'belgium': 'BE', 'italy': 'IT', 'spain': 'ES',
};
const countryCode = (val) => {
  if (!val) return '';
  const key = String(val).trim().toLowerCase();
  return COUNTRY_CODES[key] || val;
};

// Normalise a date value to a full ISO timestamp (…T…:…:….000Z) for the XML.
// Accepts "DD-MMM-YYYY", "YYYY-MM-DD", ISO strings, or "YYYY-MM-DDTHH:mm".
const MONTHS = { JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
                 JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12' };
const toXmlDateTime = (val, time) => {
  if (!val) return '';
  const str = String(val).trim();
  const t = time ? String(time).slice(0, 5) : '00:00';

  // DD-MMM-YYYY
  let m = str.match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/);
  if (m) {
    const mm = MONTHS[m[2].toUpperCase()] || '01';
    return `${m[3]}-${mm}-${m[1]}T${t}:00.000Z`;
  }
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return `${str}T${t}:00.000Z`;
  // YYYY-MM-DDTHH:mm(:ss)(.sss)(Z)
  m = str.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (m) return `${m[1]}T${m[2]}:00.000Z`;

  return str;
};

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v != null && String(v).trim() !== '') return v;
  }
  return '';
};

// Leaf element that self-closes when the value is empty (matches CHR sample style).
const leaf = (tag, val) => {
  const v = esc(val);
  return v ? `<${tag}>${v}</${tag}>` : `<${tag}/>`;
};

// ─── request builder ────────────────────────────────────────────────────────
//
// data     — the merged form values (formData/draftData from the form page)
// batches  — the Content Details rows (selected batches)
// ids      — { sponsorShipmentID, vendorShipmentID, freightForwarderScheduleID }
// pickup   — { pickupDate, pickupTime } chosen in the Schedule Pickup section

export const buildShipmentOrderCreateXml = (data = {}, batches = [], ids = {}, pickup = {}) => {
  // <...><value type="SHID"> — the Generated Shipment Schedule ID
  const shid = firstNonEmpty(ids.sponsorShipmentID, data.sponsorShipmentID);

  // Config defaults (overridable from data.* if the form/config supplies them)
  const currencyCode        = data.currencyCode || 'USD';
  const requestingCompany   = data.requestingCompanyName || '';   // customer <companyName>
  const sponsorCompany      = data.sponsorCompanyName || '';       // billTo   <companyName>
  const pickupLocationId    = data.pickupLocationId || '';
  const deliveryLocationId  = data.deliveryLocationId || '';
  const jobNumber           = firstNonEmpty(data.jobNumber, ids.vendorShipmentID); // JNO (derived)

  const pickupOpen   = toXmlDateTime(pickup.pickupDate || data.pickupDate, pickup.pickupTime || data.pickupTime);
  const deliveryOpen = toXmlDateTime(data.expectedDeliveryDate);

  const items = (batches || [])
    .filter((b) => b && !b.markForDelete)
    .map((b) => {
      // packagingType: PLT when pallets given, else the Content UoM
      const hasPallets = b.noOfPallets != null && String(b.noOfPallets).trim() !== '' && Number(b.noOfPallets) > 0;
      const packagingType = hasPallets ? 'PLT' : (b.batchUOM || 'PLT');
      // weight: Gross preferred, Net as fallback (single CHR weight)
      const weight = firstNonEmpty(b.grossWeight, b.netWeight);
      // weightUnitOfMeasure is the weight UoM (Kilograms/Pounds), NOT the content UoM
      const weightUom = b.weightUOM || 'Kilograms';
      const temp = b.temperature || 'Ambient';
      const tempSensitive = temp && String(temp).trim().toLowerCase() !== 'ambient';
      // PO Number comes from the linked PO / allocation
      const poNumber = firstNonEmpty(b.poNumber, b.scheduleAgreementNumber);
      return `        <item>
          <packagingType>${esc(packagingType)}</packagingType>
          <quantity>${esc(b.readyForDispatchQuantity ?? '')}</quantity>
          <description>${esc(b.fgMaterialName ?? '')}</description>
          <productCode>${esc(b.fgMaterialCode ?? '')}</productCode>
          <freightClass/>
          <weight>${esc(weight)}</weight>
          <weightUnitOfMeasure>${esc(weightUom)}</weightUnitOfMeasure>
          <isStackable>false</isStackable>
          <isTemperatureSensitive>${tempSensitive ? 'true' : 'false'}</isTemperatureSensitive>
          <temperatureType>${esc(temp)}</temperatureType>
          <temperatureUnit>Celsius</temperatureUnit>
          <pallets>${esc(b.noOfPallets ?? '')}</pallets>
          <palletSpaces>${esc(b.noOfPallets ?? '')}</palletSpaces>
          <referenceNumbers>
            <referenceNumber>
              <type>BATCH</type>
              <value>${esc(b.vendorBatchNumber ?? '')}</value>
            </referenceNumber>
            <referenceNumber>
              <type>PO</type>
              <value>${esc(poNumber)}</value>
            </referenceNumber>
          </referenceNumbers>
        </item>`;
    })
    .join('\n\n');

  const itemsBlock = items || `        <!-- no batches selected -->`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<Order>

  <customers>
    <customer>
      <customerCode>TBD-ASSIGNED-BY-CHR</customerCode>
      <contacts>
        <contact>
          <name>${esc(data.freightForwarderUserName)}</name>
          <type>Contact</type>
          <companyName>${esc(requestingCompany)}</companyName>
          <contactMethods>
            <contactMethod>
              <method>Phone</method>
              <value>${phoneDigits(data.freightForwarderContact)}</value>
            </contactMethod>
          </contactMethods>
        </contact>
      </contacts>
      <referenceNumbers>
        <referenceNumber>
          <type>SHID</type>
          <value>${esc(shid)}</value>
        </referenceNumber>
      </referenceNumbers>
    </customer>
  </customers>

  <billTos>
    <billTo>
      <customerCode>${esc(data.billingCentreForShipment) || 'TBD-ASSIGNED-BY-CHR'}</customerCode>
      <charges/>
      <currencyCode>${esc(currencyCode)}</currencyCode>
      <contacts>
        <contact>
          <name>${esc(data.siteUserName)}</name>
          <type>Contact</type>
          <companyName>${esc(sponsorCompany)}</companyName>
          <contactMethods>
            <contactMethod>
              <method>Phone</method>
              <value>${phoneDigits(data.siteUserContact)}</value>
            </contactMethod>
            <contactMethod>
              <method>Email</method>
              <value>${esc(data.siteUserEmail)}</value>
            </contactMethod>
          </contactMethods>
        </contact>
      </contacts>
      <referenceNumbers>
        <referenceNumber>
          <type>SHID</type>
          <value>${esc(shid)}</value>
        </referenceNumber>
      </referenceNumbers>
    </billTo>
  </billTos>

  <services>
    <service>
      <dateCalculationRule>promisedDeliveryDate</dateCalculationRule>
      <serviceLevel>S</serviceLevel>
      <sellMode>LTL</sellMode>
      <suggestedScac>TBD</suggestedScac>
      <suggestedCarrierPartyCode>TBD</suggestedCarrierPartyCode>

      <locations>
        <location>
          <customerLocationId>${esc(pickupLocationId)}</customerLocationId>
          <type>origin</type>
          <name>${esc(data.pickupOrganizationName)}</name>
          <address>
            <address1>${esc(data.pickupStreetAddress1)}</address1>
            <address2>${esc(data.pickupStreetAddress2)}</address2>
            <address3/>
            <address4/>
            <city>${esc(data.pickupCity)}</city>
            <county/>
            <lat/>
            <long/>
            ${leaf('stateProvinceCode', stateCode(data.pickupState))}
            <postalCode>${esc(data.pickupPostalCode)}</postalCode>
            <country>${esc(countryCode(data.pickupCountry))}</country>
          </address>
          <openDateTime>${pickupOpen}</openDateTime>
          <closeDateTime>${pickupOpen}</closeDateTime>
          <scheduledOpenDateTime>${pickupOpen}</scheduledOpenDateTime>
          <scheduledCloseDateTime>${pickupOpen}</scheduledCloseDateTime>
          <specialRequirement>
            <liftGate>false</liftGate>
            <insidePickup>false</insidePickup>
            <residentialNonCommercial>false</residentialNonCommercial>
            <limitedAccess>false</limitedAccess>
            <tradeShoworConvention>false</tradeShoworConvention>
            <constructionSite>false</constructionSite>
            <dropOffAtCarrierTerminal>false</dropOffAtCarrierTerminal>
          </specialRequirement>
          <scalesRequired>false</scalesRequired>
          <hasDropTrailer>false</hasDropTrailer>
          <specialInstructions/>
          <contacts>
            <contact>
              <name>${esc(data.pickupContactName)}</name>
              <type>Contact</type>
              <companyName>${esc(data.pickupOrganizationName)}</companyName>
              <contactMethods>
                <contactMethod>
                  <method>Phone</method>
                  <value>${phoneDigits(data.pickupContactNumber)}</value>
                </contactMethod>
                <contactMethod>
                  <method>Email</method>
                  <value>${esc(data.pickupContactEmail)}</value>
                </contactMethod>
              </contactMethods>
            </contact>
          </contacts>
          <referenceNumbers>
            <referenceNumber>
              <type>PU</type>
              <value>${esc(shid)}-PU</value>
            </referenceNumber>
          </referenceNumbers>
        </location>

        <location>
          <customerLocationId>${esc(deliveryLocationId)}</customerLocationId>
          <type>destination</type>
          <name>${esc(data.deliveryOrganizationName)}</name>
          <address>
            <address1>${esc(data.deliveryStreetAddress1)}</address1>
            <address2>${esc(data.deliveryStreetAddress2)}</address2>
            <address3/>
            <address4/>
            <city>${esc(data.deliveryCity)}</city>
            <county/>
            <lat/>
            <long/>
            ${leaf('stateProvinceCode', stateCode(data.deliveryState))}
            <postalCode>${esc(data.deliveryPostalCode)}</postalCode>
            <country>${esc(countryCode(data.deliveryCountry))}</country>
          </address>
          <openDateTime>${deliveryOpen}</openDateTime>
          <closeDateTime>${deliveryOpen}</closeDateTime>
          <scheduledOpenDateTime>${deliveryOpen}</scheduledOpenDateTime>
          <scheduledCloseDateTime>${deliveryOpen}</scheduledCloseDateTime>
          <specialRequirement>
            <liftGate>false</liftGate>
            <insideDelivery>false</insideDelivery>
            <residentialNonCommercial>false</residentialNonCommercial>
            <limitedAccess>false</limitedAccess>
            <tradeShoworConvention>false</tradeShoworConvention>
            <constructionSite>false</constructionSite>
            <pickupAtCarrierTerminal>false</pickupAtCarrierTerminal>
            <sortAndSegregate>false</sortAndSegregate>
          </specialRequirement>
          <scalesRequired>false</scalesRequired>
          <hasDropTrailer>false</hasDropTrailer>
          ${leaf('specialInstructions', cleanNA(data.specialInstructions))}
          <contacts>
            <contact>
              <name>${esc(data.deliveryContactName)}</name>
              <type>Contact</type>
              <companyName>${esc(data.deliveryOrganizationName)}</companyName>
              <contactMethods>
                <contactMethod>
                  <method>Phone</method>
                  <value>${phoneDigits(data.deliveryContactNumber)}</value>
                </contactMethod>
                <contactMethod>
                  <method>Email</method>
                  <value>${esc(data.deliveryContactEmail)}</value>
                </contactMethod>
              </contactMethods>
            </contact>
          </contacts>
          <referenceNumbers>
            <referenceNumber>
              <type>DEL</type>
              <value>${esc(shid)}-DEL</value>
            </referenceNumber>
          </referenceNumbers>
        </location>
      </locations>

      <items>
${itemsBlock}
      </items>

      <referenceNumbers>
        <referenceNumber>
          <type>SHID</type>
          <value>${esc(shid)}</value>
        </referenceNumber>
      </referenceNumbers>
      <isTeam>false</isTeam>
    </service>
  </services>

  <measurementSystem>Metric</measurementSystem>

  <referenceNumbers>
    <referenceNumber>
      <type>SHID</type>
      <value>${esc(shid)}</value>
    </referenceNumber>
    <referenceNumber>
      <type>JNO</type>
      <value>${esc(jobNumber)}</value>
    </referenceNumber>
  </referenceNumbers>

  <loadValues>
    <equipmentLength>52</equipmentLength>
    <equipmentWidth>9</equipmentWidth>
    <equipmentHeight>12</equipmentHeight>
    <trailerNum/>
  </loadValues>

  <onlineParties>
    <onlineParty>
      <partyType>CUSTOMER</partyType>
      <partyCode>TBD</partyCode>
      <relationshipIdentifier>${esc(shid)}</relationshipIdentifier>
    </onlineParty>
  </onlineParties>

  <notes>Created via Mosymphony Logistics Collaboration - Shipment Schedule ID Request</notes>

</Order>`;
};

// ─── response builder ───────────────────────────────────────────────────────
// The Freight Forwarder system responds with the generated order number, which
// is the Freight Forwarder Schedule ID surfaced in the success dialog.

export const buildShipmentOrderResponseXml = (ids = {}) => {
  const orderNumber = ids.freightForwarderScheduleID || '';
  return `<?xml version="1.0" encoding="UTF-8"?>
<OrderCreateResponse>
  <orderNumber>${esc(orderNumber)}</orderNumber>
</OrderCreateResponse>`;
};
